import { useQuery } from "@tanstack/react-query";
import { QRCode } from "react-qr-code";

import { api } from "@/lib/api-client";
import { useLocations } from "@/hooks/use-locations";
import { useOrganization } from "@/hooks/use-organizations";
import { useAuthStore } from "@/store/auth-store";

/** Mirrors apps/waitlist/api/schema.py::QrTokenSchema (GET /api/waitlist/qr/). */
type WaitlistQrToken = {
  location_id: string;
  token: string;
  rotation_seconds: number;
  seconds_until_next_rotation: number;
};

// Public guest-site URL (includes the ``/reservas`` base in prod). Same env
// the invitee links use; in local dev it resolves to http://reservas.localhost.
const RESERVAS_URL = (import.meta.env.VITE_RESERVAS_URL || "").replace(/\/$/, "");

/**
 * Host-stand waitlist QR — meant to live full-screen on a tablet by the door
 * so guests can scan and self-register. Editorial, calm, brand-piedra
 * background; the token rotates server-side and the query refetches just
 * before each expiry so what is on screen is always valid.
 */
export default function WaitlistQrPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);
  const { data: organization } = useOrganization(organizationId ?? "");
  const { data: locations } = useLocations();
  const location = locations?.find((l) => l.id === locationId);

  const { data, isError } = useQuery({
    queryKey: ["waitlist-qr", locationId],
    queryFn: () => api.get("waitlist/qr/").json<WaitlistQrToken>(),
    enabled: isAuthenticated && !!locationId,
    refetchInterval: (query) =>
      Math.max(3, query.state.data?.seconds_until_next_rotation ?? 30) * 1000,
    refetchIntervalInBackground: true,
  });

  const qrValue = data && RESERVAS_URL ? `${RESERVAS_URL}/lista-de-espera?t=${data.token}` : "";

  let placeholder = "Generando código…";
  if (!isAuthenticated || !locationId) placeholder = "Selecciona una sede para mostrar el QR.";
  else if (!RESERVAS_URL) placeholder = "Falta configurar VITE_RESERVAS_URL.";
  else if (isError) placeholder = "No se pudo cargar el QR. Reintentando…";

  return (
    <div className="relative flex h-dvh flex-col overflow-hidden bg-brand-piedra text-text-primary">
      {/* Top bar — brand + sede chip; minimal so the QR carries the stage. */}
      <header className="flex shrink-0 items-center justify-between px-6 py-3 sm:px-12 sm:py-5">
        <div className="flex items-baseline gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-text-secondary">
          {organization?.name && (
            <span className="font-semibold text-text-primary">{organization.name}</span>
          )}
          {organization?.name && location?.name && <span className="text-text-muted">·</span>}
          {location?.name && <span>{location.name}</span>}
        </div>
      </header>

      {/* Center stage. ``min-h-0`` lets the flex children shrink so the QR
          card can scale with the remaining height instead of pushing the
          page past the viewport on shorter screens. */}
      <main className="flex min-h-0 flex-1 flex-col items-center justify-center gap-[clamp(1rem,2.5vh,2.5rem)] px-6 pb-[clamp(1rem,3vh,2.5rem)] text-center">
        <div className="flex flex-col items-center gap-2">
          <h1 className="font-serif text-[clamp(1.75rem,5vw,4rem)] font-medium italic leading-[1.05] tracking-[-0.02em] text-text-primary">
            Escanea para anotarte
          </h1>
          <p className="max-w-xl text-[clamp(0.9rem,1.4vw,1.15rem)] leading-relaxed text-text-secondary">
            Guarda tu lugar en la lista de espera.
          </p>
        </div>

        {/* QR card — size keyed off both viewport width AND height so it
            never overflows on short screens (the editorial header and the
            steps row claim ~250-300px). */}
        <div className="flex aspect-square w-[min(72vw,60vh,520px)] items-center justify-center rounded-3xl bg-white p-4 shadow-[0_30px_60px_-20px_rgba(22,2,2,0.25)] ring-1 ring-black/5 sm:p-6">
          {qrValue ? (
            <QRCode
              value={qrValue}
              size={512}
              level="M"
              bgColor="#ffffff"
              fgColor="#160202"
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
            />
          ) : (
            <span className="px-6 text-center text-sm text-text-muted">{placeholder}</span>
          )}
        </div>

        {/* Numbered steps — quick onboarding for first-time guests. */}
        <ol className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.18em] text-text-secondary sm:text-[11px]">
          <li className="flex items-center gap-2">
            <span className="rounded-sm bg-text-primary px-1.5 py-0.5 font-bold text-brand-piedra">
              01
            </span>
            Escanea
          </li>
          <li className="flex items-center gap-2">
            <span className="rounded-sm bg-text-primary px-1.5 py-0.5 font-bold text-brand-piedra">
              02
            </span>
            Completa tus datos
          </li>
          <li className="flex items-center gap-2">
            <span className="rounded-sm bg-text-primary px-1.5 py-0.5 font-bold text-brand-piedra">
              03
            </span>
            Espera tu turno
          </li>
        </ol>
      </main>

      {/* Dev-only inspector strip: the live URL the QR encodes + the
          rotation countdown so we can verify what the guest is about to
          scan AND know how long the current value is good for. Hidden in
          production via the Vite DEV flag. */}
      {import.meta.env.DEV && qrValue && (
        <div className="absolute bottom-4 left-4 right-4 flex flex-col items-center gap-1.5 sm:flex-row sm:justify-between">
          <a
            href={qrValue}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all rounded-md border border-black/10 bg-white/80 px-2 py-1 font-mono text-[10px] leading-snug text-text-secondary backdrop-blur hover:text-text-primary"
            title="Abrir lista en una pestaña nueva"
          >
            {qrValue}
          </a>
          {typeof data?.seconds_until_next_rotation === "number" && (
            <span className="shrink-0 rounded-md border border-black/10 bg-white/80 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-text-secondary backdrop-blur">
              rota en {data.seconds_until_next_rotation}s · cada {data.rotation_seconds}s
            </span>
          )}
        </div>
      )}
    </div>
  );
}
